import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DataTable, Pagination } from 'grommet';
import { columnDefinition } from './ColumnsHelper';
import { Box, Text } from '/src/components';
import { ApiData } from '/src/lib/models';
import { TResponseMetadata } from '/src/lib/types';

const ApiDataTableBase: React.FC<ApiDataTableProps> = (props) => {
  const { apiData, shownColumns, onSort, sortProperty, sortDirection, metadata, perPage, currentPage, onPageChange } =
    props;

  const { t } = useTranslation();

  const hasData = apiData && apiData.length > 0;
  const hasColumns = shownColumns.length > 0;

  const columns = useMemo(() => shownColumns.map((name) => columnDefinition(name)), [shownColumns]);

  const sanitizedData = useMemo<Array<ApiData & { _rowId: string }>>(() => {
    return (
      apiData?.map((item, index) => {
        const explicitId: string =
          (item.session_id && String(item.session_id).trim()) ||
          (item.serial_number && String(item.serial_number).trim()) ||
          `row_${index}`;

        return {
          ...item,
          // Uses existing identifier if available, otherwise falls back to index
          _rowId: explicitId,
        };
      }) || []
    );
  }, [apiData]);

  const handlePageChange = (page: number) => {
    onPageChange(page);
  };

  /** Render **/
  return (
    <>
      <Box overflow={{ horizontal: 'auto' }}>
        {hasColumns && hasData && (
          <DataTable
            id="api_data_table"
            style={{ width: '100%' }}
            pad={{ horizontal: '0.5rem' }}
            verticalAlign={{ body: 'middle' }}
            step={perPage || 20}
            columns={columns}
            data={sanitizedData}
            primaryKey="_rowId"
            sortable
            sort={sortProperty && sortDirection ? { property: sortProperty, direction: sortDirection } : undefined}
            onSort={onSort ? (sort) => onSort(sort.property, sort.direction) : undefined}
            background={['light-6', 'white']}
            border={{ color: 'light-2', side: 'bottom', size: 'small' }}
          />
        )}
        {(!hasColumns || !hasData) && (
          <Box pad={{ horizontal: '1.5rem', vertical: '2rem' }} background="light-6" justify="center">
            <Text alignSelf="center" size="medium" fontFamily="Lato, sans-serif">
              {t(hasColumns ? 'apiData:noData' : 'apiData:noColumns')}
            </Text>
          </Box>
        )}
      </Box>
      {hasColumns && hasData && !!metadata && !!currentPage && (
        <Box pad="1.5rem" flex="grow">
          <Pagination
            alignSelf="center"
            size="small"
            page={currentPage}
            step={metadata?.per_page}
            numberItems={metadata?.total}
            onChange={(e) => handlePageChange(e.page)}
          />
        </Box>
      )}
    </>
  );
};

export const ApiDataTable = React.memo(ApiDataTableBase);

export type ApiDataTableProps = {
  apiData?: ApiData[];
  shownColumns: string[];
  metadata?: TResponseMetadata;
  perPage?: number;
  currentPage: number;
  onPageChange: (page: number) => void;
  onSort?: (property: string, direction: 'asc' | 'desc') => void;
  sortProperty?: string;
  sortDirection?: 'asc' | 'desc';
};
